import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React, { useEffect, useRef } from 'react';
import { Provider } from 'react-redux';
import { store } from "../../store";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { resetViews, setClientId, setCountry, setEnv, setLocale, fetchCountry } from "../../store/reducer";
import { View } from "../../store/types";
import Login from "../Login";
import Mfa from "../Mfa";
import ResetPassword from "../ResetPassword";
import Signup from "../Signup";
import { ForterSnippet } from "../../lib/snippets";
import { FlipCard, FlipCardInner, FlipCardWrapper, OneAuthContainer, ProfileLogo } from "./OneAuth.styles";
import { register, unregister } from "../../lib/clientCallback";
import { getClientId, setClientId as sdkSetClient } from '@chegg/one-auth-sdk';
import { EventTypes, recordEvents, setEnv as setNewRelicEnv } from "../../utils/newRelicEvents";
import config from "../../config";
import { useInjectPX3rdPartyScript } from "../hooks/useInjectPX3rdPartyScript";
import { useGetContent } from "../hooks/useGetContent";
import Cookies from 'js-cookie';
import { LANG_PREFERENCE_COOKIE } from "../../lib/contentful/types";
var _require = require("../../../package.json"),
  version = _require.version;
var OneAuth = function OneAuth(props) {
  var dataTest = props['data-test'],
    className = props.className,
    options = props.options,
    view = props.view,
    callback = props.callback,
    styles = props.styles;
  var _useAppSelectors = useAppSelectors(),
    env = _useAppSelectors.env,
    clientId = _useAppSelectors.clientId,
    country = _useAppSelectors.country,
    showBack = _useAppSelectors.showBack,
    frontView = _useAppSelectors.frontView,
    backView = _useAppSelectors.backView;
  var dispatch = useAppDispatch();
  var ref = useRef(null);
  var langPreferenceCookie = Cookies.get(LANG_PREFERENCE_COOKIE) || undefined;
  useGetContent(langPreferenceCookie); // fetch translations from contentful

  useEffect(function () {
    var client = getClientId();
    if (client !== clientId) {
      sdkSetClient(clientId);
    }
  }, [clientId]);
  useEffect(function () {
    register(callback);
    return function () {
      return unregister();
    };
  }, [callback]);
  useEffect(function () {
    recordEvents(_defineProperty({}, EventTypes.init, {
      version: version
    }));
  }, []);
  useEffect(function () {
    dispatch(resetViews({
      view: view && view || View.SIGNIN
    }));
  }, [view, dispatch]);

  /**
   * this useEffect is mainly for making the component listen to changes via storybook controls and sync thier values in the state
   * it listens to prop changes (triggered from storybook/other client) and will rerender as needed
   */
  useEffect(function () {
    if (props.env && props.env !== env) {
      dispatch(setEnv({
        env: props.env
      }));
      setNewRelicEnv(props.env);
    }
    if (props.clientId && props.clientId !== clientId) {
      dispatch(setClientId({
        clientId: props.clientId
      }));
    }
    if (props.locale) {
      dispatch(setLocale({
        locale: props.locale
      }));
    }
  }, [env, clientId, dispatch, props.env, props.locale, props.clientId]);
  useEffect(function () {
    if (props.country && props.country !== country) {
      dispatch(setCountry({
        country: props.country
      }));
    } else if (!props.country && !country) {
      dispatch(fetchCountry());
    }
  }, [country, props.country, dispatch]);
  useEffect(function () {
    scrollOneAuthIntoView(ref);
  }, [frontView, showBack, backView]);
  useInjectPX3rdPartyScript(config[env].pxAppId);
  var classNames = ['auth-container', className].filter(Boolean).join(' ');

  // if (!content) return <>Loading...</>;

  return /*#__PURE__*/React.createElement(OneAuthContainer, {
    ref: ref,
    "data-test": dataTest || 'oneAuth',
    className: classNames
  }, /*#__PURE__*/React.createElement(FlipCardWrapper, {
    className: showBack ? 'showBack' : ''
  }, /*#__PURE__*/React.createElement(FlipCardInner, {
    className: "FlipCardInner"
  }, renderCards())), /*#__PURE__*/React.createElement(Mfa, null), /*#__PURE__*/React.createElement(ForterSnippet, {
    siteId: config[env].forterSiteId,
    euToken: config[env].forterEuToken
  }));
  function renderCards() {
    if (!frontView) {
      return null;
    }
    var viewToRender = showBack && backView || frontView;
    return [frontView, backView].map(function (view, index) {
      var classNames = [viewToRender !== view ? 'empty' : undefined, index === 0 ? 'frontView' : 'backView'];
      return /*#__PURE__*/React.createElement(FlipCard, {
        key: view,
        className: classNames.join(' ')
      }, viewToRender === view && renderCard(view) || null, renderFooterImage());
    });
  }
  function renderCard(view) {
    switch (view) {
      case View.SIGNIN:
        return /*#__PURE__*/React.createElement(Login, {
          options: options === null || options === void 0 ? void 0 : options.signin,
          styles: styles === null || styles === void 0 ? void 0 : styles.signin
        });
      case View.SIGNUP:
        return /*#__PURE__*/React.createElement(Signup, {
          options: options === null || options === void 0 ? void 0 : options.signup,
          styles: styles === null || styles === void 0 ? void 0 : styles.signup
        });
      case View.RESET_PASSWORD:
        return /*#__PURE__*/React.createElement(ResetPassword, {
          options: options === null || options === void 0 ? void 0 : options.resetPassword,
          styles: styles === null || styles === void 0 ? void 0 : styles.resetpassword
        });
    }
  }
  function renderFooterImage() {
    var _options$footer;
    if (!(options !== null && options !== void 0 && (_options$footer = options.footer) !== null && _options$footer !== void 0 && _options$footer.image)) return null;
    return /*#__PURE__*/React.createElement(ProfileLogo, {
      "data-test": "footer_image",
      alt: "logo",
      src: options.footer.image
    });
  }
};
function scrollOneAuthIntoView(ref) {
  if (ref.current) {
    var position = ref.current.getBoundingClientRect();
    if (position && Math.sign(position.y) === -1) {
      ref.current.scrollIntoView();
    }
  }
}
function useAppSelectors() {
  return {
    env: useAppSelector(function (state) {
      return state.env;
    }),
    clientId: useAppSelector(function (state) {
      return state.clientId;
    }),
    frontView: useAppSelector(function (state) {
      return state.frontView;
    }),
    backView: useAppSelector(function (state) {
      return state.backView;
    }),
    showBack: useAppSelector(function (state) {
      return state.showBack;
    }),
    country: useAppSelector(function (state) {
      return state.country;
    })
  };
}
var OneAuthWrapper = function OneAuthWrapper(props) {
  return /*#__PURE__*/React.createElement(Provider, {
    store: store
  }, /*#__PURE__*/React.createElement(OneAuth, props));
};
OneAuthWrapper.displayName = 'OneAuth';
export { OneAuth };
export default OneAuthWrapper;